import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Paper, CurrencyField} from 'views/components';
import {Translate, translate} from 'views/module';

import moment from 'moment';

const Price = (props) => {
  const {setIsStepValid, newJob, updateJobPrice} = props;

  const hourlyRate = newJob?.price?.hourlyRate ?? '';

  // Calculate weeklyHours and weeklyEarnings together using useMemo
  const scheduleMetrics = React.useMemo(() => {
    const scheduleType = newJob?.schedule?.type || '';
    const scheduleData = newJob?.schedule?.schedule || {};
    let formattedSchedule = {
      type: scheduleType,
    };

    if (scheduleType === 'RECURRENCE_EVENT') {
      formattedSchedule = {
        type: scheduleType,
        recurrenceRule: {
          weeklySlots: scheduleData.weeklySlots || [],
          timezone: scheduleData.timezone || '',
        },
      };
    }

    if (scheduleType === 'LIVEIN_EVENT') {
      formattedSchedule = {
        type: scheduleType,
      };
    }

    if (scheduleType === 'FLEXIBLE') {
      formattedSchedule = {
        type: scheduleType,
        flexibleSchedule: {
          preferredDays: scheduleData.preferredDays || [],
          preferredTimes: scheduleData.preferredTimes || [],
        },
      };

      if ((scheduleData.visitDuration || '').trim()) {
        formattedSchedule.flexibleSchedule.visitDuration = scheduleData.visitDuration.trim();
      }
    }

    const rate = parseFloat(hourlyRate) || 0;

    if (!formattedSchedule?.type) {
      return {
        totalHours: 0,
        shiftCount: 0,
        weeklyPay: null,
        hasEstimate: false,
      };
    }

    if (formattedSchedule.type === 'LIVEIN_EVENT') {
      const totalHours = 56;
      return {
        totalHours,
        shiftCount: 7,
        weeklyPay: totalHours * rate,
        hasEstimate: true,
      };
    }

    if (formattedSchedule.type === 'RECURRENCE_EVENT') {
      const weeklySlots = formattedSchedule.recurrenceRule?.weeklySlots || [];
      const totalHours = weeklySlots.reduce((total, slot) => {
        const start = moment(slot.startTime);
        const end = moment(slot.endTime);
        const hoursPerSlot = end.diff(start, 'hours', true);

        return total + Math.abs(hoursPerSlot);
      }, 0);

      return {
        totalHours,
        shiftCount: weeklySlots.length,
        weeklyPay: totalHours * rate,
        hasEstimate: true,
      };
    }

    if (formattedSchedule.type === 'FLEXIBLE') {
      const flexibleSchedule = formattedSchedule.flexibleSchedule || {};
      const shiftCount = (flexibleSchedule.preferredDays || []).length;
      const durationText = `${flexibleSchedule.visitDuration ?? ''}`.trim();
      const parsedHours = Number.parseFloat(durationText.replace(/[^0-9.]/g, ''));
      const totalHours = (Number.isFinite(parsedHours) ? parsedHours : 0) * shiftCount;
      const hasEstimate = totalHours > 0 && shiftCount > 0;

      return {
        totalHours,
        shiftCount,
        weeklyPay: hasEstimate ? totalHours * rate : null,
        hasEstimate,
      };
    }

    return {
      totalHours: 0,
      shiftCount: 0,
      weeklyPay: null,
      hasEstimate: false,
    };
  }, [newJob?.schedule, hourlyRate]);

  const setHourlyRate = (hourlyRate) => {
    updateJobPrice((prev) => ({
      ...prev,
      hourlyRate: hourlyRate,
    }));
  };


  // Validate step - requires hourly rate to be an integer > 0
  React.useEffect(() => {
    if (hourlyRate === '' || hourlyRate === null || hourlyRate === undefined) {
      setIsStepValid(false);
    } else {
      const rate = parseFloat(hourlyRate);
      const isInteger = Number.isInteger(rate);
      setIsStepValid(!isNaN(rate) && isInteger && rate > 0);
    }
  }, [setIsStepValid, hourlyRate]);

  return (
    <Box sx={{p: 2}}>
      <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 2}}>
        <Translate expression="career.create.Price.Title" />
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{mb: 3, textAlign: 'center'}}>
        <Translate expression="career.create.Price.Subtitle" />
      </Typography>

      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3}}>
        {/* Hourly Rate Input */}
        <Box sx={{width: '100%', maxWidth: '600px', textAlign: 'center'}}>
          <Typography variant="subtitle1" gutterBottom sx={{fontWeight: 600}}>
            <Translate expression="career.create.Price.HourlyRateLabel" />
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <Translate expression="career.create.Price.HourlyRateDescription" />
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center'}}>
            <Typography variant="h6">$</Typography>
            <CurrencyField
              value={hourlyRate}
              onChange={setHourlyRate}
              placeholder="35"
              style={{
                width: '120px',
                fontSize: '18px',
              }}
            />
            <Typography variant="body1" color="text.secondary">/hr</Typography>
          </Box>
        </Box>

        {/* Weekly Earnings Estimate - Always Show */}
        <Paper
          sx={{
            p: 3,
            backgroundColor: '#fef7f7',
            border: '1px solid #fee2e2',
            textAlign: 'center',
            width: '100%',
            maxWidth: '600px',
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: '#1f2937',
              fontWeight: 600,
            }}
          >
            <Translate expression="career.create.Price.WeeklyEstimateTitle" />
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: '#dc2626',
              fontWeight: 700,
              mb: 1,
            }}
          >
            <Translate
              expression="career.create.Price.WeeklyEarningsDisplay"
              substitute={{amount: (scheduleMetrics.weeklyPay || 0).toFixed(2)}}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {
              hourlyRate &&
              scheduleMetrics.hasEstimate &&
              translate('career.create.Price.WeeklyEstimateCalculation', {
                hours: scheduleMetrics.totalHours.toFixed(1),
                rate: parseFloat(hourlyRate).toFixed(2),
              })
            }
            {
              hourlyRate &&
              !scheduleMetrics.hasEstimate &&
              translate('career.create.Price.WeeklyEstimateVaries')
            }
            {
              !hourlyRate &&
              translate('career.create.Price.WeeklyEstimateDefault')
            }
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

Price.propTypes = {
  setIsStepValid: PropTypes.func.isRequired,
  newJob: PropTypes.object.isRequired,
  updateJobPrice: PropTypes.func.isRequired,
};

export default Price;
